import React, { useEffect } from "react";
import "@/App.css";
import "@/index.css";
import "@/pages/PortalProposal.css";
import "@/components/ValuePanel.css";
import {
    BrowserRouter,
    Navigate,
    Route,
    Routes,
    useLocation,
    useParams,
} from "react-router-dom";

import AdminLogin from "@/pages/AdminLogin";
import AdminSignup from "@/pages/AdminSignup";
import AdminSubmissions from "@/pages/AdminSubmissions";
import AdminSubmissionDetail from "@/pages/AdminSubmissionDetail";
import AdminAccessRequests from "@/pages/AdminAccessRequests";
import AdminUsers from "@/pages/AdminUsers";
import AdminUserDetail from "@/pages/AdminUserDetail";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import Landing from "@/pages/Landing";
import Employers from "@/pages/Employers";
import Employees from "@/pages/Employees";
import HowItWorks from "@/pages/HowItWorks";
import About from "@/pages/About";
import FAQ from "@/pages/FAQ";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Apply from "@/pages/Apply";
import ApplyForm from "@/pages/ApplyForm";
import TeamApply from "@/pages/TeamApply";
import EmployerCensus from "@/pages/EmployerCensus";
import PortalProposal from "@/pages/PortalProposal";
import PortalLogin from "@/pages/PortalLogin";
import Portal from "@/pages/Portal";
import PortalEmployer from "@/pages/PortalEmployer";
import PortalEmployerQuotes from "@/pages/PortalEmployerQuotes";
import PortalEmployee from "@/pages/PortalEmployee";
import PortalSignin from "@/pages/PortalSignin";
import PortalErrorBoundary from "@/components/portal/PortalErrorBoundary";
import EmployerSignIn from "@/pages/EmployerSignIn";
import SetPassword from "@/pages/SetPassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Nominate from "@/pages/Nominate";
import Unsubscribed from "@/pages/Unsubscribed";
import Placeholder from "@/pages/Placeholder";
import AskSuccessTeamFab from "@/components/AskSuccessTeamFab";
import {
    captureFromUrl,
    trackLandingOnce,
} from "@/lib/attribution";
import { rawHttp } from "@/lib/api";
import { track } from "@/lib/analytics";

/**
 * Captures `?ref=/?src=` on every navigation + fires a one-time
 * landing tracker for each unique (ref, src) pair in the session.
 */
function AttributionCapture() {
    const location = useLocation();
    useEffect(() => {
        const stamp = captureFromUrl();
        if (!stamp) return;
        trackLandingOnce(rawHttp, location.pathname).then((a) => {
            if (a) {
                track("share_land", { source: a.src, share_id: a.ref });
            }
        });
    }, [location.pathname, location.search]);
    return null;
}

/**
 * Phase 3a §2 — Legacy URL shim.
 *
 * Pre-Phase-3a links used `/portal/{employer_id}` and `/portal/{employer_id}/census`.
 * Canonical routes now live under `/portal/employer/{employer_id}[/census]`. SPA-level
 * redirects get the same user-visible effect; a genuine HTTP 301 would require
 * ingress config (flagged as a limitation in PRD).
 *
 * Fires a `portal.url_migrated` analytics event on the first redirect so we can
 * retire the shim safely.
 */
function LegacyPortalRedirect({ kind }) {
    const { employerId } = useParams();
    // Guard against matching reserved sub-routes: never redirect "employer",
    // "login", or "proposal" -- those are canonical or distinct routes.
    if (!employerId || ["employer", "login", "proposal"].includes(employerId)) {
        return <Navigate to="/portal" replace />;
    }
    const to =
        kind === "census"
            ? `/portal/employer/${employerId}/census`
            : `/portal/employer/${employerId}`;
    try {
        track("portal.url_migrated", { from: kind, employer_id: employerId });
    } catch (_) { /* analytics is best-effort */ }
    return <Navigate to={to} replace />;
}


/**
 * Phase 3c §2 — SPA-level 301 shim for the old auth-entry URLs. Preserves
 * query strings so `?invite=…` / `?err=…` survives the hop.
 */
function AuthRedirect({ to }) {
    const location = useLocation();
    const target = `${to}${location.search || ""}`;
    useEffect(() => {
        try { track("auth.url_migrated", { from: location.pathname, to }); } catch (_) { /* noop */ }
    }, [location.pathname, to]);
    return <Navigate to={target} replace />;
}

/**
 * Marketing chrome is hidden on authenticated surfaces and their legacy
 * transient routes. The `PortalChrome` wrapper provides its own header
 * + footer for portal pages; admin pages render their own chrome inside
 * `/admin/*`.
 *
 * When this list grows past ~8 prefixes, migrate to layout routes
 * (<Route element={<MarketingLayout/>}> vs <Route element={<PortalLayout/>}>)
 * — see discovery report.
 */
const HIDE_MARKETING_CHROME_PREFIXES = [
    "/admin",
    // Authenticated portal sub-routes. `/portal/login` and `/portal/signin`
    // are intentionally NOT here — they're unauthenticated entry surfaces
    // and belong to the marketing shell.
    "/portal/employer",
    "/portal/employee",
    "/portal/proposal",
    "/team/apply",      // employer wizard (authenticated)
    "/team/verify",     // transient magic-link landing
    "/apply/form",      // employee wizard (authenticated)
    "/employee/verify", // transient magic-link landing
];

function shouldHideMarketingChrome(pathname) {
    return HIDE_MARKETING_CHROME_PREFIXES.some(
        (p) => pathname === p || pathname.startsWith(p + "/"),
    );
}

function ChromeGate({ children }) {
    const location = useLocation();
    const hideChrome = shouldHideMarketingChrome(location.pathname);
    return (
        <>
            {!hideChrome && <Nav />}
            <main>{children}</main>
            {!hideChrome && <Footer />}
            {/* Phase 2f — Level-3 help. The component self-hides on admin/auth routes. */}
            {!hideChrome && <AskSuccessTeamFab />}
        </>
    );
}

export default function App() {
    return (
        <div className="App" data-testid="app-root">
            <BrowserRouter>
                <ScrollToTop />
                <AttributionCapture />
                <ChromeGate>
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/employers" element={<Employers />} />
                        <Route path="/how-it-works" element={<HowItWorks />} />
                        <Route path="/employees" element={<Employees />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/faq" element={<FAQ />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/apply" element={<Apply />} />
                        <Route path="/apply/form" element={<ApplyForm />} />
                        {/* Phase 3c polish — direct nominate route, bypasses
                            the /apply state machine so the /employees nominate
                            card lands on the form immediately. */}
                        <Route path="/nominate" element={<Nominate />} />
                        {/* Phase 3d — redirect target for the one-click
                            unsubscribe token on prospective-employees
                            marketing emails. */}
                        <Route path="/unsubscribed" element={<Unsubscribed />} />
                        {/* Phase 3 (unified entry) — /signin is retired.
                            All "access my plan" routes now funnel to /apply,
                            which is the canonical employee email-lookup surface. */}
                        <Route path="/signin" element={<AuthRedirect to="/apply" />} />
                        <Route path="/employer-signin" element={<EmployerSignIn />} />
                        <Route path="/set-password" element={<SetPassword />} />
                        {/* Phase A4 — password recovery + setup-email routes.
                            /forgot-password?role=employer|employee triggers a
                            reset email; /reset-password?token=...&role=...
                            &context=forgot|setup lands the recipient. */}
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        {/* Phase 3c §2 — 301-style shims preserving query strings. */}
                        <Route path="/employee/login" element={<AuthRedirect to="/apply" />} />
                        <Route path="/team/login" element={<AuthRedirect to="/employer-signin" />} />
                        {/* Phase A6 — retired magic-link surfaces. The JSX pages are
                            deleted; these SPA redirects preserve old bookmarks and
                            stale email links. The verify routes land on /apply with
                            a banner that explains the link expired (handled inline). */}
                        <Route path="/team/login-legacy" element={<AuthRedirect to="/employer-signin" />} />
                        <Route path="/employee/login-legacy" element={<AuthRedirect to="/apply" />} />
                        <Route path="/team/verify" element={<Navigate to="/apply?reason=link_expired" replace />} />
                        <Route path="/team/apply" element={<PortalErrorBoundary><TeamApply /></PortalErrorBoundary>} />
                        {/* Phase 3a — Session-resolved portal root */}
                        <Route path="/portal" element={<PortalErrorBoundary><Portal /></PortalErrorBoundary>} />
                        {/* Phase 3c §2 → Phase 3 (unified) — retired disambiguation
                            page. Redirects all the way through to /apply. */}
                        <Route path="/portal/signin" element={<AuthRedirect to="/apply" />} />
                        <Route path="/portal/signin-legacy" element={<PortalSignin />} />
                        {/* Phase 3a canonical employer portal routes */}
                        {/* Phase 3e — quotes dashboard MVP. Must be above
                            the :employerId param route so the static
                            "quotes" segment wins. */}
                        <Route path="/portal/employer/quotes" element={<PortalErrorBoundary><PortalEmployerQuotes /></PortalErrorBoundary>} />
                        <Route path="/portal/employer/:employerId" element={<PortalErrorBoundary><PortalEmployer /></PortalErrorBoundary>} />
                        <Route path="/portal/employer/:employerId/census" element={<PortalErrorBoundary><EmployerCensus /></PortalErrorBoundary>} />
                        {/* Phase 3b canonical employee portal route */}
                        <Route path="/portal/employee/:employeeId" element={<PortalErrorBoundary><PortalEmployee /></PortalErrorBoundary>} />
                        {/* Phase A6 — /employee/verify retired; preserve old bookmarks. */}
                        <Route path="/employee/verify" element={<Navigate to="/apply?reason=link_expired" replace />} />
                        {/* Phase 3c §4 — Laguna admin access-request queue */}
                        <Route path="/admin/access-requests" element={<AdminAccessRequests />} />
                        {/* Phase 3a — Legacy URL migration (SPA-level 301-style shims).
                            Legacy `/portal/{employer_id}` and `/portal/{employer_id}/census`
                            remain reachable via `<Navigate replace />` to the canonical routes.
                            Placed AFTER the canonical `/portal/employer/*` routes so the
                            static `employer` segment is matched first. */}
                        <Route path="/portal/:employerId/census" element={<LegacyPortalRedirect kind="census" />} />
                        <Route path="/portal/login" element={<PortalLogin />} />
                        <Route path="/portal/proposal/:id" element={<PortalErrorBoundary><PortalProposal /></PortalErrorBoundary>} />
                        <Route path="/portal/:employerId" element={<LegacyPortalRedirect kind="home" />} />
                        <Route path="/admin" element={<AdminLogin />} />
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin/signin" element={<AdminLogin />} />
                        <Route path="/admin/signup" element={<AdminSignup />} />
                        <Route path="/admin/submissions" element={<AdminSubmissions />} />
                        <Route path="/admin/submissions/:id" element={<AdminSubmissionDetail />} />
                        {/* Phase A5 — admin users directory + detail */}
                        <Route path="/admin/users" element={<AdminUsers />} />
                        <Route path="/admin/users/:role/:id" element={<AdminUserDetail />} />
                        <Route
                            path="*"
                            element={<Placeholder slug="about" />}
                        />
                    </Routes>
                </ChromeGate>
            </BrowserRouter>
        </div>
    );
}
